
import * as React from "react"

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
}

export type Breakpoint = keyof typeof BREAKPOINTS

export function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState<boolean>(false)

  React.useEffect(() => {
    const mediaQuery = window.matchMedia(query)
    
    const handleChange = () => {
      setMatches(mediaQuery.matches)
    }
    
    // Set initial value
    setMatches(mediaQuery.matches)
    
    // Add listener
    mediaQuery.addEventListener("change", handleChange)
    
    // Clean up
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [query])

  return matches
}

export function useIsMobile() {
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`)
  return isMobile
}

export function useBreakpoint(breakpoint: Breakpoint) {
  const matches = useMediaQuery(`(min-width: ${BREAKPOINTS[breakpoint]}px)`)
  return matches
}

export function useActiveBreakpoint() {
  const [activeBreakpoint, setActiveBreakpoint] = React.useState<Breakpoint | null>(null)
  
  React.useEffect(() => {
    const checkBreakpoint = () => {
      const width = window.innerWidth
      
      if (width >= BREAKPOINTS["2xl"]) {
        setActiveBreakpoint("2xl")
      } else if (width >= BREAKPOINTS.xl) {
        setActiveBreakpoint("xl")
      } else if (width >= BREAKPOINTS.lg) {
        setActiveBreakpoint("lg")
      } else if (width >= BREAKPOINTS.md) {
        setActiveBreakpoint("md")
      } else if (width >= BREAKPOINTS.sm) {
        setActiveBreakpoint("sm")
      } else {
        setActiveBreakpoint(null) // smaller than sm
      }
    }
    
    checkBreakpoint()
    window.addEventListener("resize", checkBreakpoint)
    
    return () => window.removeEventListener("resize", checkBreakpoint)
  }, [])
  
  return activeBreakpoint
}
